import type { MechanicDefinition } from "./types";

/** D&D 5e condition effects reference */
// ⭐ auditPasses: 1 — applied at definition below

const DND_CONDITIONS: Record<string, string> = {
  blinded: "Can't see. Auto-fail sight checks. Attack rolls have disadvantage. Attackers have advantage.",
  charmed: "Can't attack or target the charmer with harmful abilities. Charmer has advantage on social checks.",
  deafened: "Can't hear. Auto-fail hearing checks.",
  frightened: "Disadvantage on checks/attacks while source is in sight. Can't willingly move closer to source.",
  grappled: "Speed becomes 0. Ends if grappler is incapacitated or target is forced out of reach.",
  incapacitated: "Can't take actions or reactions.",
  invisible: "Impossible to see without special sense. Attack rolls have advantage. Attackers have disadvantage.",
  paralyzed: "Incapacitated + can't move or speak. Auto-fail STR/DEX saves. Attackers have advantage. Melee hits are auto-crits.",
  petrified: "Transformed to stone. Weight ×10. Incapacitated. Resistance to all damage. Immune to poison/disease.",
  poisoned: "Disadvantage on attack rolls and ability checks.",
  prone: "Disadvantage on attacks. Melee attackers have advantage. Ranged attackers have disadvantage. Costs half movement to stand.",
  restrained: "Speed 0. Attacks have disadvantage. Attackers have advantage. Disadvantage on DEX saves.",
  stunned: "Incapacitated + can't move + can speak only falteringly. Auto-fail STR/DEX saves. Attackers have advantage.",
  unconscious: "Incapacitated + can't move or speak + unaware. Drop held items + fall prone. Auto-fail STR/DEX saves. Attackers have advantage. Melee hits are auto-crits.",
  exhaustion_1: "Disadvantage on ability checks.",
  exhaustion_2: "Speed halved.",
  exhaustion_3: "Disadvantage on attack rolls and saving throws.",
  exhaustion_4: "Hit point maximum halved.",
  exhaustion_5: "Speed reduced to 0.",
  exhaustion_6: "Death.",
  concentration: "Maintaining a concentration spell. Taking damage requires CON save (DC = max(10, damage/2)). Fail = spell ends.",
};

export const conditions: MechanicDefinition = {
  id: "conditions",
  disabled: true,
  name: "Conditions & Status Effects",
  description: "Track D&D conditions (Poisoned, Stunned, Prone, etc.) with duration and mechanical effects",
  icon: "ShieldAlert",
  category: "systems",
  auditPasses: 1, // ⭐
  aiHints: {
    purpose: "Tracks status conditions (poisoned, stunned, prone, etc.) with durations and mechanical penalties.",
    whenToUse: "D&D-style games or any world with combat where status effects matter.",
    synergies: ["combat", "saving_throws", "magic_system"],
    conflicts: [],
    prerequisites: [],
    tags: ["conditions", "status-effects", "debuffs", "buffs", "d&d"],
    complexity: 2,
    exampleConfig: { use_dnd_conditions: true },
    stateVariables: ["active_conditions", "exhaustion_level"],
  },
  configSchema: [
    {
      key: "condition_set",
      label: "Condition Set",
      type: "select",
      options: [
        { value: "dnd_full", label: "Full D&D 5e Conditions", description: "All 14 standard conditions plus exhaustion tiers" },
        { value: "simplified", label: "Simplified (6 core)", description: "Poisoned, Stunned, Prone, Frightened, Blinded, Restrained only" },
        { value: "custom", label: "Custom conditions", description: "Define your own conditions below" },
      ],
      default: "dnd_full",
    },
    {
      key: "exhaustion_tiers",
      label: "Exhaustion System",
      type: "boolean",
      default: true,
      description: "Track 6-tier exhaustion (links to Survival mechanic if active)",
    },
    {
      key: "auto_expire",
      label: "Auto-Expire Conditions",
      type: "boolean",
      default: true,
      description: "Conditions with set durations auto-expire via postTurnHook",
    },
  ],
  systemPromptSection: (config) => {
    const set = config.condition_set || "dnd_full";
    const exhaustion = config.exhaustion_tiers !== false;
    const condList = set === "simplified"
      ? "Poisoned, Stunned, Prone, Frightened, Blinded, Restrained"
      : "Blinded, Charmed, Deafened, Frightened, Grappled, Incapacitated, Invisible, Paralyzed, Petrified, Poisoned, Prone, Restrained, Stunned, Unconscious";

    const condEffects = Object.entries(DND_CONDITIONS)
      .filter(([k]) => set === "dnd_full" || ["poisoned","stunned","prone","frightened","blinded","restrained"].includes(k))
      .filter(([k]) => !k.startsWith("exhaustion_") || exhaustion)
      .map(([k, v]) => `  - ${k.toUpperCase()}: ${v}`)
      .join("\n");

    return `## CONDITIONS & STATUS EFFECTS (Hard Mechanic)
- Available conditions: ${condList}
- Use apply_condition to add, remove_condition to clear early.
- Conditions with duration auto-expire each turn.
- ALWAYS check active_conditions before attacks/saves and apply penalties.

### CONDITION EFFECTS
${condEffects}

${exhaustion ? `### EXHAUSTION (6-tier, cumulative)
- Each tier adds its penalty ON TOP of previous tiers.
- L1: disadvantage on checks. L2: speed halved. L3: disadvantage on attacks/saves. L4: HP max halved. L5: speed 0. L6: death.
- Long rest reduces exhaustion by 1 level. Survival needs at 0 = gain 1 exhaustion per turn.
- Use apply_condition with condition="exhaustion" and set the exhaustion_level in the source field.` : ""}

### CONCENTRATION
- If a caster is concentrating on a spell and takes damage, call saving_throw(target, 'constitution', max(10, damage/2)).
- On failure, the spell ends. Only one concentration spell at a time.
- Track via apply_condition(target, 'concentration', duration=999, source='spell_name').`;
  },
  widgets: [
    {
      id: "conditions_status",
      type: "status_bar_item",
      label: "Conditions",
      icon: "ShieldAlert",
      component: "ConditionsWidget",
      dataBindings: { conditions: "variables.active_conditions" },
      visibility: "when_data",
      priority: 8,
    },
    {
      id: "conditions_inline",
      type: "inline",
      label: "Conditions",
      icon: "ShieldAlert",
      component: "ConditionsWidget",
      dataBindings: { conditions: "variables.active_conditions" },
      visibility: "when_data",
      priority: 12,
    },
  ],
  initializeState: () => ({
    active_conditions: [],
  }),
  postTurnHook: (context) => {
    const conditions = context.state.variables?.active_conditions || [];
    if (conditions.length === 0) return null;

    const autoExpire = context.config.auto_expire !== false;
    if (!autoExpire) return null;

    const updated: any[] = [];
    const expired: string[] = [];
    for (const c of conditions) {
      if (c.duration_turns != null && c.duration_turns > 0) {
        const remaining = c.duration_turns - 1;
        if (remaining <= 0) {
          expired.push(c.name);
        } else {
          updated.push({ ...c, duration_turns: remaining });
        }
      } else {
        updated.push(c); // no duration = permanent until removed
      }
    }

    if (expired.length === 0) return null;
    return {
      stateUpdates: { active_conditions: updated },
      narrativeNotes: expired.map((n) => `✨ Condition expired: ${n}`),
    };
  },
  onEvent: (event) => {
    // React to rest_completed — clear short-duration conditions
    if (event.type === "rest_completed" && event.data.rest_type === "long_rest") {
      return {
        narrativeNotes: ["💤 Long rest: all short-duration conditions cleared. Exhaustion reduced by 1."],
      };
    }
    return null;
  },
  tools: [
    {
      type: "function",
      function: {
        name: "apply_condition",
        description: "Apply a status condition to a target. Duration in turns (0 = permanent until removed). Stacks exhaustion levels.",
        parameters: {
          type: "object",
          properties: {
            target: { type: "string", description: "Entity name or 'player'" },
            condition: { type: "string", description: "Condition name (e.g. poisoned, stunned, prone, exhaustion)" },
            duration_turns: { type: "number", description: "Duration in turns. 0 or omit for permanent." },
            source: { type: "string", description: "What caused the condition (spell name, trap, poison, etc.)" },
          },
          required: ["target", "condition"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "remove_condition",
        description: "Remove a condition from a target. Use for dispelling, curing, or standing up from prone.",
        parameters: {
          type: "object",
          properties: {
            target: { type: "string", description: "Entity name or 'player'" },
            condition: { type: "string", description: "Condition to remove" },
            reason: { type: "string", description: "Why it's being removed (spell ends, cured, stood up, etc.)" },
          },
          required: ["target", "condition"],
        },
      },
    },
  ],
};
